import type { dashboard } from '@cwos/api-client';

/**
 * WHAT A FIGURE MEANS, IN WORDS — deterministic text built from the data on
 * screen, never from a model. Every sentence here is either fixed copy about a
 * definition (OPERATIONAL-SERVICE-METRICS.md) or arithmetic over the series the
 * chart is drawing, so the explanation cannot say something the picture does
 * not. "Why" questions go to Ask Phibian, which has the tools to answer them.
 *
 * Pure functions: unit-tested without a DOM.
 */

export interface MetricAbout {
  /** What one unit of the figure is. */
  counts: string;
  /** Where the rows come from. */
  source: string;
}

const RITTEN_CANONICAL =
  'Ritten visits (occurrences), counted by the canonical delivered-services rule.';

export const METRIC_ABOUT = {
  servicesDelivered: {
    counts:
      'Each delivered visit once: complete, started (empty) or incomplete in Ritten, or a blank past visit ' +
      'whose encounter recorded minutes. A group visit counts once.',
    source: RITTEN_CANONICAL,
  },
  serviceHours: {
    counts:
      'Minutes the client attended on delivered visits (the linked encounter’s minutes when the visit has ' +
      'none), in hours. This is service time, not hours worked.',
    source: RITTEN_CANONICAL,
  },
  clientsServed: {
    counts: 'Distinct clients with at least one delivered visit.',
    source: RITTEN_CANONICAL,
  },
  workersDelivering: {
    counts:
      'Distinct staff credited on a delivered visit. Every staff member on a group visit is credited. ' +
      'Never “clocked in”.',
    source: RITTEN_CANONICAL,
  },
  activeWorkers: {
    counts:
      'Workers on the roster who are employed and active, whether or not they delivered a service in ' +
      'this period.',
    source: 'The workforce roster (the EHR staff mapping).',
  },
  hoursWorked: {
    counts: 'Hours on the time clock. Never service hours.',
    source: 'Phibian’s time clock (clock-in and clock-out).',
  },
  scheduled: {
    counts: 'Visits on the calendar that were not cancelled and are still listed in Ritten.',
    source: RITTEN_CANONICAL,
  },
  completion: {
    counts:
      'Delivered visits out of the visits that were due: not cancelled, still listed in Ritten, and either ' +
      'already settled in Ritten or more than 48 hours past their start. A past visit nobody started counts ' +
      'as due and not delivered.',
    source: RITTEN_CANONICAL,
  },
  cancelled: {
    counts:
      'Visits Ritten cancelled. A visit Ritten cancelled and then stopped listing is still a cancellation.',
    source: RITTEN_CANONICAL,
  },
  noShow: {
    counts: 'Visits Ritten recorded as a no-show.',
    source: RITTEN_CANONICAL,
  },
  notStarted: {
    counts:
      'Visits that were due (more than 48 hours past their start) with no outcome recorded: not ' +
      'delivered, not cancelled, not a no-show.',
    source: RITTEN_CANONICAL,
  },
  unlisted: {
    counts:
      'Visits Ritten no longer lists and did not cancel. They are not counted as scheduled or as ' +
      'cancelled.',
    source: 'Ritten calendar sync (the visit disappeared from Ritten’s answer).',
  },
  careGaps: {
    counts:
      'Clients whose delivered service in a week fell below their active, duration-stated service ' +
      'target, beside the number of clients who have such a target.',
    source: 'Service targets on file, measured against canonical delivered services.',
  },
} satisfies Record<string, MetricAbout>;

/**
 * The care-gap trend with no target on file: the widget's own sentence
 * (widgetDataService `care_gaps` no_denominator), so the two surfaces word the
 * same blank the same way.
 */
export const NO_TARGETS_SENTENCE =
  'No care-gap figure to show — no client has an active, duration-stated service target on file ' +
  'for this period, so there is no plan to measure delivery against. This is not a zero.';

export type MetricAboutKey = keyof typeof METRIC_ABOUT;

/** Each Overview headline, in the one vocabulary above. */
export const HEADLINE_ABOUT: Record<dashboard.HeadlineMetricKey, MetricAboutKey> = {
  appointments: 'servicesDelivered',
  serviceHours: 'serviceHours',
  clientsServed: 'clientsServed',
  workersDelivering: 'workersDelivering',
  activeWorkers: 'activeWorkers',
  hoursWorked: 'hoursWorked',
};

/** The measured value of a weekly point, or null for any kind of absence. */
const valueOf = (p: dashboard.TrendPoint | undefined): number | null =>
  p?.kind === 'value' ? p.value : null;

/**
 * THE SUM OF A WEEKLY SERIES — only when every point is a measurement.
 *
 * For an ADDITIVE series (services, service hours, scheduled, due, delivered)
 * the server bounds every week by the period, so this equals the KPI over the
 * same window. A distinct count (clients, workers) is not additive and must
 * never be summed. A hidden or unmeasured week makes the sum unknown, not
 * smaller, so it answers null rather than a partial total.
 */
export function seriesTotal(series: dashboard.TrendSeries): number | null {
  let total = 0;
  for (const p of series.points) {
    const v = valueOf(p);
    if (v === null) return null;
    total += v;
  }
  return total;
}

/**
 * What changed: the latest COMPLETE week against the complete week before it.
 * A week to date or a week the period clips is never compared — that is how a
 * partial week reads as a collapse.
 */
export function weekChangeSentence(
  series: dashboard.TrendSeries,
  coverage: readonly dashboard.WeekCoverage[],
  labels: readonly string[],
  fmt: (v: number) => string,
): string {
  const full = series.points.flatMap((p, i) => {
    const v = valueOf(p);
    return v !== null && (coverage[i] ?? 'full') === 'full' ? [{ i, v }] : [];
  });
  const last = full[full.length - 1];
  if (!last) return 'There is no complete week in this period yet, so there is nothing to compare.';
  const prev = full[full.length - 2];
  const head = `Week of ${labels[last.i]}: ${fmt(last.v)}`;
  if (!prev) return `${head}. There is no earlier complete week in this period to compare it with.`;
  const delta = last.v - prev.v;
  if (delta === 0) return `${head}, the same as the week of ${labels[prev.i]}.`;
  return `${head}, ${delta > 0 ? 'up' : 'down'} ${fmt(Math.abs(delta))} from ${fmt(prev.v)} in the week of ${labels[prev.i]}.`;
}

/**
 * Needs attention: the latest complete week sits well below the usual week —
 * under three quarters of the median of the complete weeks before it. Null when
 * nothing stands out or there is too little history to say (fewer than three
 * earlier complete weeks).
 */
export function belowUsualSentence(
  series: dashboard.TrendSeries,
  coverage: readonly dashboard.WeekCoverage[],
  labels: readonly string[],
  fmt: (v: number) => string,
): string | null {
  const full = series.points.flatMap((p, i) => {
    const v = valueOf(p);
    return v !== null && (coverage[i] ?? 'full') === 'full' ? [{ i, v }] : [];
  });
  const last = full[full.length - 1];
  const before = full.slice(0, -1).map((x) => x.v);
  if (!last || before.length < 3) return null;
  const sorted = [...before].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
  if (median <= 0 || last.v >= 0.75 * median) return null;
  return (
    `The week of ${labels[last.i]} (${fmt(last.v)}) is well below the usual week (${fmt(median)}). ` +
    'Open that week’s records to see which visits were not delivered.'
  );
}

/** "88 of 107 (82%)" — the percent computed from exactly the two figures shown. */
export function ofLabel(part: number, whole: number, fmt: (v: number) => string = String): string {
  if (whole <= 0) return `${fmt(part)} of ${fmt(whole)}`;
  return `${fmt(part)} of ${fmt(whole)} (${Math.round((part / whole) * 100)}%)`;
}
